/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import {
  decodeProtectedHeader,
  jwtVerify,
  JWK,
  JWSHeaderParameters,
  KeyLike,
  importJWK,
} from "jose";
import { User } from "oidc-client-ts";
import {
  MissingJWKSURLError,
  UnreachableURIError,
  UnreachableOpenIDProviderURIError,
  UnreachableJWKSURIError,
  EmptyJWKSError,
} from "./oidc-errors";
import { refreshAuthToken } from "./oidc-refresh";

export {
  MissingJWKSURLError,
  UnreachableURIError,
  UnreachableOpenIDProviderURIError,
  UnreachableJWKSURIError,
  EmptyJWKSError,
} from "./oidc-errors";

export declare interface OIDCProviderSettings {
  /**
   * The URL of the OIDC/OAuth2 provider
   */
  authority: string;
}

export default class OIDCService {
  private configuration: OIDCProviderSettings;
  private jwksURI: string | null;

  constructor(configuration: OIDCProviderSettings) {
    this.configuration = configuration;
    this.jwksURI = null;
  }

  public getJKWSURI(): string | null {
    return this.jwksURI;
  }

  // Helper method to fetch OIDC configuration
  private async fetchOIDCConfiguration(): Promise<void> {
    const oidcConfigurationURL: string = `${this.configuration.authority}/.well-known/openid-configuration`;

    try {
      const response: Response = await fetch(oidcConfigurationURL);

      if (!response.ok) {
        throw new UnreachableOpenIDProviderURIError(oidcConfigurationURL);
      }

      const data = (await response.json()) as any;
      this.jwksURI = data.jwks_uri;
    } catch (error) {
      if (error instanceof RTCError) {
        throw new UnreachableOpenIDProviderURIError(oidcConfigurationURL);
      }
      throw error;
    }
  }

  // Helper method to validate JWKS URI
  private validateJWKSUri(): void {
    if (!this.jwksURI) {
      throw new MissingJWKSURLError(this.configuration.authority);
    }
  }

  protected async getOIDCConfiguration(): Promise<string> {
    if (!this.jwksURI) {
      await this.fetchOIDCConfiguration();
    }

    this.validateJWKSUri();
    return this.jwksURI!;
  }

  protected async getSigningKeys(): Promise<JWK[]> {
    const jwksURI: string = await this.getOIDCConfiguration();

    try {
      const response: Response = await fetch(jwksURI);
      const data: any = await response.json();

      if (!response.ok) throw new UnreachableJWKSURIError(jwksURI);

      return data.keys;
    } catch (error) {
      if (error instanceof RTCError) {
        throw new UnreachableJWKSURIError(jwksURI);
      }

      throw error;
    }
  }

  // Helper method to find signing key by kid
  private findSigningKeyByKid(keys: JWK[], kid: string): JWK | undefined {
    return keys.find((key) => key.kid === kid);
  }

  // Helper method to get default signing key
  private getDefaultSigningKey(keys: JWK[]): JWK | undefined {
    return keys.length > 0 ? keys[0] : undefined;
  }

  // Helper method to select signing key
  private selectSigningKey(keys: JWK[], header: JWSHeaderParameters): JWK {
    if (header.kid) {
      const key = this.findSigningKeyByKid(keys, header.kid);
      if (key) {
        return key;
      }
    }

    const defaultKey = this.getDefaultSigningKey(keys);
    if (!defaultKey) {
      throw new EmptyJWKSError();
    }

    return defaultKey;
  }

  protected async getSigningKey(header: JWSHeaderParameters): Promise<KeyLike> {
    const keys: JWK[] = await this.getSigningKeys();
    const signingKey = this.selectSigningKey(keys, header);

    // Ensure TypeScript understands that this will be a KeyLike
    return importJWK(signingKey, "RS256") as Promise<KeyLike>;
  }

  public async validateToken(token: string): Promise<boolean> {
    let result: boolean = true;

    try {
      const key = await this.getSigningKey(decodeProtectedHeader(token));

      const localConfiguration = {
        issuer: this.configuration.authority,
      };

      await jwtVerify(token, key, localConfiguration);
    } catch (error) {
      if (error instanceof UnreachableURIError) {
        throw error;
      }

      result = false;
    }

    return result;
  }

  public async refreshToken(): Promise<User | null | undefined> {
    return refreshAuthToken(this.configuration.authority);
  }
}

import { getRequiredEnvVar } from '@shared/utils/validate-env';
import { FF_MOCK_AUTH } from '@shared/utils/feature-flag';

// Only initialize OIDC service if not in mock mode
const USE_MOCK_AUTH = FF_MOCK_AUTH;
let oidcService: OIDCService | undefined;

if (!USE_MOCK_AUTH) {
  const REACT_APP_AUTHORITY_URL: string = getRequiredEnvVar('REACT_APP_AUTHORITY_URL');
  oidcService = new OIDCService({authority: REACT_APP_AUTHORITY_URL});
}

export { oidcService };
